import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { MapPin, Calendar as CalendarIcon, Clock, Star, Plane, TrendingUp, Building2, Coins } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import destinationPlaceholder from "@/assets/destination-placeholder.jpg";
import CountryDetailPage from "./CountryDetailPage";
import { useToast } from "@/hooks/use-toast";
import { HelpTooltip, NestPointsHelp } from "@/components/ui/help-tooltip";

interface Country {
  id: string;
  name: string;
  photo_url?: string;
  banner_url?: string;
  image_url?: string;
  flag_url?: string;
  tagline?: string;
  description?: string;
  tourist_stay_default?: string;
  remote_work_visa_option?: string;
  notes_1_5_months?: number;
  ease_1_5_months?: number;
  ease_compliance?: number;
  cost_of_living?: number;
  attractiveness_rating?: number;
  employee_notes?: string;
  weekly_cost_excluding_flight?: number;
  round_trip_flight_cost_from_france?: number;
  renest_notes?: string;
  total_score?: number;
  timezone_utc_offset?: string;
}

interface CountryWithPoints extends Country {
  points_per_week: number;
  total_points_needed: number;
  can_afford: boolean;
  timezone_difference: number;
  distance_category: string;
  is_allowed: boolean;
  restriction_reason?: string;
}

interface SearchFilters {
  departureCountry: string;
  departureDate: Date | undefined;
  duration: number;
  destination: string;
}

interface EmployeeBalance {
  total_points: number;
  used_points: number;
  available_points: number;
}

const DEFAULT_FALLBACK = destinationPlaceholder;

const ImageWithFallback = ({ src, alt, className, fallback = DEFAULT_FALLBACK, ...props }: any) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      setImgSrc(fallback);
    }
  };

  useEffect(() => {
    setImgSrc(src);
    setHasError(false);
  }, [src]);

  return <img src={imgSrc} alt={alt} className={className} loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={handleError} {...props} />;
};

const ExploreTab = ({ employee }: { employee: any }) => {
  const [trendingDestinations, setTrendingDestinations] = useState<CountryWithPoints[]>([]);
  const [popularDestinations, setPopularDestinations] = useState<CountryWithPoints[]>([]);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [employeeBalance, setEmployeeBalance] = useState<EmployeeBalance | null>(null);
  const [filters, setFilters] = useState<SearchFilters>({
    departureCountry: "",
    departureDate: undefined,
    duration: 1,
    destination: ""
  });
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { toast } = useToast();

  // Early return if employee is not loaded
  if (!employee) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="ml-3 text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  useEffect(() => {
    if (employee?.profile_id) {
      fetchEmployeeData();
    }
  }, [employee]);

  useEffect(() => {
    if (companyId) {
      fetchDestinations();
    }
  }, [companyId]);

  const fetchEmployeeData = async () => {
    if (!employee?.profile_id) {
      console.error('Employee profile_id missing');
      setLoading(false);
      return;
    }

    try {
      // Get employee's company
      const { data: employeeData, error: empError } = await supabase
        .from('employees')
        .select('id, company_id')
        .eq('profile_id', employee.profile_id)
        .maybeSingle();

      if (empError) {
        console.error('Error fetching employee:', empError);
        setLoading(false);
        return;
      }

      if (!employeeData) {
        console.error('No employee data found');
        setLoading(false);
        return;
      }

      setCompanyId(employeeData.company_id);

      // Get employee's current balance
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();

      const { data: pointsData } = await supabase
        .from('employee_points')
        .select('total_points, used_points')
        .eq('employee_id', employeeData.id)
        .eq('month', currentMonth)
        .eq('year', currentYear)
        .maybeSingle();

      if (pointsData) {
        setEmployeeBalance({
          total_points: pointsData.total_points,
          used_points: pointsData.used_points,
          available_points: pointsData.total_points - pointsData.used_points
        });
      }
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setLoading(false);
    }
  };

  const fetchDestinations = async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      // Fetch countries opened for the company
      const { data: countries, error } = await supabase
        .from('countries')
        .select(`
          *,
          company_countries!inner(is_open)
        `)
        .eq('company_countries.company_id', companyId)
        .eq('company_countries.is_open', true)
        .eq('is_active', true);

      if (error) throw error;

      // Calculate points for each destination (for 1 week)
      const destinationsWithPoints = await Promise.all(
        (countries || []).map(async (country) => {
          try {
            const { data, error } = await supabase.rpc('get_country_points_for_company', {
              p_company_id: companyId,
              p_country_id: country.id,
              p_duration_weeks: 1
            });

            if (error) {
              console.error('Error calculating points:', error);
              return null;
            }

            const calculation = data as any;

            if (!calculation.allowed) {
              return {
                ...country,
                points_per_week: 0,
                total_points_needed: 0,
                can_afford: false,
                timezone_difference: calculation.timezone_difference || 0,
                distance_category: '',
                is_allowed: false,
                restriction_reason: calculation.reason
              };
            }

            return {
              ...country,
              points_per_week: calculation.points_per_week,
              total_points_needed: calculation.total_points,
              can_afford: employeeBalance ? calculation.total_points <= employeeBalance.available_points : false,
              timezone_difference: calculation.timezone_difference,
              distance_category: calculation.distance_category,
              is_allowed: true
            };
          } catch (err) {
            console.error('Error processing country:', err);
            return null;
          }
        })
      );

      const validDestinations = destinationsWithPoints.filter((d) => d !== null) as CountryWithPoints[];
      
      // Sort by attractiveness for trending
      const trending = [...validDestinations].sort((a, b) => 
        (b.attractiveness_rating || 0) - (a.attractiveness_rating || 0)
      ).slice(0, 6);
      
      setTrendingDestinations(trending);
      
      // For popular destinations, we'll show the same for now (in production, this would be based on actual booking data)
      setPopularDestinations(trending);
      
    } catch (error: any) {
      console.error('Error fetching destinations:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les destinations"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // Filter logic can be implemented here if needed
    toast({
      title: "Recherche en cours",
      description: "Filtrage des destinations selon vos critères..."
    });
  };

  const renderDestinationCard = (destination: CountryWithPoints, rank?: number) => (
    <Card 
      key={destination.id} 
      className="overflow-hidden cursor-pointer transition-all hover:shadow-lg group"
      onClick={() => {
        setSelectedCountry(destination);
        setIsDetailOpen(true);
      }}
    >
      <div className="relative h-64">
        <ImageWithFallback
          src={destination.image_url || destination.banner_url || destination.photo_url || DEFAULT_FALLBACK}
          alt={destination.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {rank && (
          <div className="absolute top-4 left-4 bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg shadow-lg">
            #{rank}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <h3 className="font-bold text-2xl mb-2">{destination.name}</h3>
          <p className="text-sm text-white/90 mb-4">{destination.tagline}</p>
          <div className="flex items-center justify-between">
            <Badge className="bg-primary/90 backdrop-blur-sm text-primary-foreground border-0">
              <Coins className="h-4 w-4 mr-1" />
              {destination.points_per_week} Nest Points / semaine
            </Badge>
            <Button 
              variant="secondary" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCountry(destination);
                setIsDetailOpen(true);
              }}
            >
              Découvrir
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="ml-3 text-muted-foreground">Chargement des destinations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-8">
      {/* Balance Display */}
      {employeeBalance && (
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  Votre solde Nest Points
                  <HelpTooltip content={NestPointsHelp.howPointsWork} />
                </p>
                <p className="text-3xl font-bold flex items-center gap-2">
                  <Coins className="h-8 w-8 text-primary" />
                  {employeeBalance.available_points}
                  <span className="text-sm text-muted-foreground font-normal">points disponibles</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total: {employeeBalance.total_points} pts</p>
                <p className="text-sm text-muted-foreground">Utilisés: {employeeBalance.used_points} pts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search/Filter Section */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-2xl font-bold text-foreground mb-2 flex items-center gap-2">
            Planifiez votre prochaine workation
            <HelpTooltip content={NestPointsHelp.planningBudget} />
          </h2>
          <p className="text-sm text-muted-foreground mb-6">{NestPointsHelp.pointCosts}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Pays de départ */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Pays de départ</label>
              <Select
                value={filters.departureCountry}
                onValueChange={(value) => setFilters((prev) => ({ ...prev, departureCountry: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="france">🇫🇷 France</SelectItem>
                  <SelectItem value="spain">🇪🇸 Espagne</SelectItem>
                  <SelectItem value="germany">🇩🇪 Allemagne</SelectItem>
                  <SelectItem value="italy">🇮🇹 Italie</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date de départ */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Date de départ</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filters.departureDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filters.departureDate ? format(filters.departureDate, "PPP", { locale: fr }) : "Choisir une date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.departureDate}
                    onSelect={(date) => setFilters((prev) => ({ ...prev, departureDate: date }))}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Durée */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Durée du voyage</label>
              <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-background">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">1 semaine</span>
              </div>
            </div>

            {/* Destination */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Destination</label>
              <Input
                placeholder="Où souhaitez-vous aller ?"
                value={filters.destination}
                onChange={(e) => setFilters((prev) => ({ ...prev, destination: e.target.value }))}
              />
            </div>
          </div>

          <Button className="mt-6 w-full md:w-auto" onClick={handleSearch}>
            <Star className="mr-2 h-4 w-4" />
            Rechercher des destinations
          </Button>
        </CardContent>
      </Card>

      {/* No destinations message */}
      {trendingDestinations.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <MapPin className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucune destination disponible</h3>
            <p className="text-muted-foreground">
              Contactez votre administrateur pour ouvrir des pays pour votre entreprise
            </p>
          </CardContent>
        </Card>
      )}

      {/* Trending Destinations Carousel */}
      {trendingDestinations.length > 0 && (
        <section>
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold text-foreground">Nos voyages tendances</h2>
          </div>
          
          <Carousel className="w-full">
            <CarouselContent className="-ml-4">
              {trendingDestinations.map((destination) => (
                <CarouselItem key={destination.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  {renderDestinationCard(destination)}
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex" />
            <CarouselNext className="hidden md:flex" />
          </Carousel>
        </section>
      )}

      {/* Popular Destinations in Company */}
      {popularDestinations.length > 0 && (
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold text-foreground">Les voyages les plus populaires dans votre entreprise</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularDestinations.slice(0, 3).map((destination, index) => (
              renderDestinationCard(destination, index + 1)
            ))}
          </div>
        </section>
      )}

      {/* Country Detail Dialog */}
      {selectedCountry && (
        <CountryDetailPage
          country={selectedCountry}
          open={isDetailOpen}
          onClose={() => {
            setIsDetailOpen(false);
            setSelectedCountry(null);
          }}
        />
      )}
    </div>
  );
};

export default ExploreTab;
